'use client';

import { useMemo, useState } from 'react';
import { Search, ArrowRight, ArrowLeft } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Card, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { TypographyMuted, TypographyOverline, TypographySmall } from '@/components/ui/typography';
import { Reveal } from '@/components/motion/reveal';
import type { BlogCategory } from '@/content/blog-types';
import { BlogCardMedia, BlogCardMeta, type BlogCard } from '@/components/sections/blog-card-parts';
import {
  BlogTopicFilterButton,
  type BlogTopic,
} from '@/components/sections/blog-topic-filter-button';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 5;

export type { BlogTopic };

export interface PopularTopic {
  key: BlogCategory;
  label: string;
  countLabel: string;
}

interface Labels {
  searchPlaceholder: string;
  sortLatest: string;
  sortOldest: string;
  readMore: string;
  empty: string;
  popularTopics: string;
  stillLookingHeading: string;
  stillLookingText: string;
  helpCenter: string;
  prevPage: string;
  nextPage: string;
}

interface Props {
  posts: BlogCard[];
  topics: BlogTopic[];
  popularTopics: PopularTopic[];
  labels: Labels;
  helpHref: string;
}

function ListRow({ post, readMore }: { post: BlogCard; readMore: string }) {
  return (
    <Link href={`/blog/${post.slug}`} className="group block">
      <Card className="group flex flex-col gap-0 overflow-hidden py-0 shadow-card transition-brand group-hover:-translate-y-1 group-hover:shadow-raised sm:flex-row sm:items-start border-border/60">
        <BlogCardMedia
          post={post}
          sizes="(min-width: 1024px) 20rem, (min-width: 640px) 18rem, 92vw"
          imageClassName="object-center"
          className="sm:w-72 lg:w-80"
        />
        <CardContent className="flex flex-1 flex-col p-6">
          <TypographyOverline className="text-brand-gold">{post.categoryLabel}</TypographyOverline>
          <CardTitle className="mt-2 text-h4 font-medium text-primary">{post.title}</CardTitle>
          <CardDescription className="mt-2 line-clamp-2">{post.description}</CardDescription>
          <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5">
            <BlogCardMeta post={post} />
            <TypographySmall className="inline-flex items-center gap-1.5 text-brand-gold">
              {readMore}
              <ArrowRight
                aria-hidden
                data-directional-icon-decorative
                className="size-4 transition-transform group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1"
              />
            </TypographySmall>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export function BlogIndexClient({ posts, topics, labels }: Props) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState<'all' | BlogCategory>('all');
  const [sort, setSort] = useState<'latest' | 'oldest'>('latest');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = posts.filter((p) => {
      if (active !== 'all' && p.category !== active) return false;
      if (q && !`${p.title} ${p.description}`.toLowerCase().includes(q)) return false;
      return true;
    });
    list.sort((a, b) =>
      sort === 'latest' ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date),
    );
    return list;
  }, [posts, active, query, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function reset() {
    setPage(1);
  }

  return (
    <section className="pb-[var(--space-section)] pt-3 sm:pt-5">
      <div className="container-brand">
        {/* Search + sort */}
        <Reveal className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                reset();
              }}
              placeholder={labels.searchPlaceholder}
              aria-label={labels.searchPlaceholder}
              className="h-11 rounded-full bg-card ps-10 border-border/60 focus-visible:ring-ring/40"
            />
          </div>
          <Select
            value={sort}
            onValueChange={(v) => {
              setSort(v as 'latest' | 'oldest');
              reset();
            }}
          >
            <SelectTrigger className="!h-11 rounded-full bg-card border-border/60 focus-visible:ring-ring/40 sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest">{labels.sortLatest}</SelectItem>
              <SelectItem value="oldest">{labels.sortOldest}</SelectItem>
            </SelectContent>
          </Select>
        </Reveal>

        {/* Topic tags */}
        <Reveal index={1} className="mt-6 flex flex-wrap gap-3">
          {topics.map((topic) => (
            <BlogTopicFilterButton
              key={topic.key}
              topic={topic}
              active={active === topic.key}
              onSelect={(key) => {
                setActive(key);
                reset();
              }}
              className="h-11 gap-2 px-5"
            />
          ))}
        </Reveal>

        {/* Articles */}
        <div className="mt-10">
          <div>
            {paged.length === 0 ? (
              <Card className="border-border/60 shadow-card">
                <CardContent className="p-10">
                  <TypographyMuted className="text-center">{labels.empty}</TypographyMuted>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {paged.map((post) => (
                  <ListRow key={post.slug} post={post} readMore={labels.readMore} />
                ))}
              </div>
            )}

            {/* Pagination */}
            <div className="mt-10 flex items-center justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label={labels.prevPage}
                className="border-border bg-card text-muted-foreground hover:text-foreground disabled:opacity-40"
              >
                <ArrowLeft
                  className="size-4 rtl:-scale-x-100"
                  aria-hidden
                  data-directional-icon-decorative
                />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <Button
                  key={n}
                  type="button"
                  variant={n === currentPage ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setPage(n)}
                  aria-current={n === currentPage ? 'page' : undefined}
                  className={cn(
                    'text-small font-medium',
                    // `text-small` (a size util) otherwise wins the tailwind-merge
                    // conflict against the variant's `text-primary-foreground` and
                    // leaves the active number charcoal-on-green (unreadable).
                    n === currentPage
                      ? 'text-primary-foreground'
                      : 'border-border bg-card text-foreground hover:text-primary',
                  )}
                >
                  {n}
                </Button>
              ))}
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                aria-label={labels.nextPage}
                className="border-border bg-card text-muted-foreground hover:text-foreground disabled:opacity-40"
              >
                <ArrowRight
                  className="size-4 rtl:-scale-x-100"
                  aria-hidden
                  data-directional-icon-decorative
                />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
